
import { ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, appCheck } from '../firebaseConfig';
import { getToken } from 'firebase/app-check';
import { checkSubscriptionRenewal, getBillingGateway } from '../services';
import CurrencyDropdown from './CurrencyDropdown';
import SeriousSeekerSection from './SeriousSeekerSection';
import HomeTestimonials from './HomeTestimonials';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../utils/navigation';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { getPricingDefaultValue } from '../utils/pricingI18n';
import './componentDarkSurfaceFixes.css';

interface Plan {
  name: string;
  monthlyPrice: string;
  yearlyPrice: string;
  period: string;
  features: string[];
  monthlyPlanId?: string;
  yearlyPlanId?: string;
  popular?: boolean;
}

function StrokeCheck() {
  return (
    <span aria-hidden="true" className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
        <path
          d="M2 7.5 5.5 10.5 12 3.5"
          stroke="#2563eb"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

const plansUSD: Plan[] = [
  {
    name: 'free_plan',
    monthlyPrice: '$0',
    yearlyPrice: '$0',
    period: '/month',
    features: [], // Will be populated by getTranslatedFeatures
  },
  {
    name: 'Explorer',
    monthlyPrice: '$19.99',
    yearlyPrice: '$199',
    monthlyPlanId: 'plan_SNku4CQRrh0bEl',
    yearlyPlanId: 'plan_SNl0YQdeuosITV',
    period: '/month',
    features: [], // Will be populated by getTranslatedFeatures
    popular: true,
  },
  {
    name: 'Scholar',
    monthlyPrice: '$39.99',
    yearlyPrice: '$399',
    monthlyPlanId: 'plan_SNkwBT3rmQ6Is9',
    yearlyPlanId: 'plan_SNl217UXbVtV6w',
    period: '/month',
    features: [], // Will be populated by getTranslatedFeatures
  },
  {
    name: 'Sage',
    monthlyPrice: '$79.99',
    yearlyPrice: '$799',
    monthlyPlanId: 'plan_SNkxSFFL1uLomA',
    yearlyPlanId: 'plan_SNl2ixnX9firod',
    period: '/month',
    features: [], // Will be populated by getTranslatedFeatures
  },
  {
    name: 'Enlightened',
    monthlyPrice: 'Custom',
    yearlyPrice: 'Custom',
    period: '',
    features: [], // Will be populated by getTranslatedFeatures
  },
];

const plansINR: Plan[] = [
  {
    name: 'free_plan',
    monthlyPrice: '₹0',
    yearlyPrice: '₹0',
    period: '/month',
    features: [],
  },
  {
    name: 'Explorer',
    monthlyPrice: '₹499',
    yearlyPrice: '₹4,990',
    monthlyPlanId: 'plan_SNku4CQRrh0bEl',
    yearlyPlanId: 'plan_SNl0YQdeuosITV',
    period: '/month',
    features: [],
    popular: true,
  },
  {
    name: 'Scholar',
    monthlyPrice: '₹999',
    yearlyPrice: '₹9,990',
    monthlyPlanId: 'plan_SNkwBT3rmQ6Is9',
    yearlyPlanId: 'plan_SNl217UXbVtV6w',
    period: '/month',
    features: [],
  },
  {
    name: 'Sage',
    monthlyPrice: '₹1,999',
    yearlyPrice: '₹19,990',
    monthlyPlanId: 'plan_SNkxSFFL1uLomA',
    yearlyPlanId: 'plan_SNl2ixnX9firod',
    period: '/month',
    features: [],
  },
  {
    name: 'Enlightened',
    monthlyPrice: 'Custom',
    yearlyPrice: 'Custom',
    period: '',
    features: [],
  },
];

const topUpPlanUSD: Plan = {
  name: 'Top-Up',
  monthlyPrice: '$9.99',
  yearlyPrice: '$9.99',
  period: '/one-time',
  features: [], // Will be populated by getTranslatedFeatures
};

const topUpPlanINR: Plan = {
  name: 'Top-Up',
  monthlyPrice: '₹249',
  yearlyPrice: '₹249',
  period: '/one-time',
  features: [],
};

// Testimonials will be generated using translation keys in the component

export default function HPricingSection() {
  const [isAnnual, setIsAnnual] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [remainingHits, setRemainingHits] = useState<string | number>('0');
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isCurrentPlanMonthly, setIsCurrentPlanMonthly] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>('USD');
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation(['pricing', 'features', 'testimonials']);
  const tp = (key: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue: getPricingDefaultValue(key), ...(options ?? {}) });
  const localizedPath = useLocalizedPath();

  const getDynamicTokenFeature = (planName: string) => {
    if (planName === 'Explorer') {
      return currency === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '43,200', perMonth: '3,600' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '3,600' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '150,000', perMonth: '12,500' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '12,500' });
    }

    if (planName === 'Scholar') {
      return currency === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '86,400', perMonth: '7,200' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '7,200' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '300,000', perMonth: '25,000' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '25,000' });
    }

    if (planName === 'Sage') {
      return currency === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '172,800', perMonth: '14,400' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '14,400' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '600,000', perMonth: '50,000' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '50,000' });
    }

    if (planName === 'Top-Up') {
      return tp('pricing.token_labels.add_output_tokens_one_time', {
        count: currency === 'INR' ? '1,800' : '6,000',
      });
    }

    return null;
  };

  const getPlanDisplayPrice = (plan: Plan) =>
    plan.name === 'Enlightened' ? tp('pricing.custom_pricing') : isAnnual ? plan.yearlyPrice : plan.monthlyPrice;

  const getPlanDisplayPeriod = (planName: string) => {
    if (planName === 'Enlightened') {
      return '';
    }

    if (planName === 'Top-Up') {
      return tp('pricing.one_time');
    }

    return isAnnual ? t('pricing.per_year') : t('pricing.per_month');
  };

  const getPlanCopyKey = (planName: string) =>
    planName === 'free_plan' ? 'free' : planName.toLowerCase();

  const getPlanSubtitle = (planName: string) =>
    tp(`pricing.plan_subtitles.${getPlanCopyKey(planName)}`);

  const getPlanDescription = (planName: string) =>
    planName === 'Enlightened'
      ? tp('pricing.plan_descriptions.enlightened')
      : '';

  // Helper function to get translated features for each plan
  const getTranslatedFeatures = (planName: string) => {
    const features: string[] = [];
    const baseKey = planName === 'free_plan' ? 'free' : planName.toLowerCase();
    const maxFeatures = planName === 'Top-Up' ? 1 : 9;
    
    for (let i = 1; i <= maxFeatures; i++) {
      const featureKey = `pricing.${baseKey}_features.feature${i}`;
      const translatedFeature = t(featureKey);
      if (translatedFeature !== featureKey) {
        features.push(translatedFeature);
      }
    }

    const dynamicTokenFeature = getDynamicTokenFeature(planName);
    if (features.length && dynamicTokenFeature) {
      features[0] = dynamicTokenFeature;
    }
    if (currency === 'INR') {
      return features.map((feature) =>
        /AI Studio/i.test(feature)
          ? t('pricing.explorer_features.feature_day_quality', { defaultValue: 'Day Quality & Muhurta timeline' })
          : feature
      );
    }
    return features;
  };

  const getPresentedFeatures = (planName: string) => {
    const features = getTranslatedFeatures(planName);

    if (planName === 'free_plan') {
      return {
        label: tp('pricing.feature_labels.includes'),
        features,
      };
    }

    if (planName === 'Enlightened') {
      return {
        label: tp('pricing.feature_labels.built_for_institutions'),
        features,
      };
    }

    if (['Explorer', 'Scholar', 'Sage'].includes(planName) && features.length > 1) {
      return {
        label: features[1].replace(/:$/, ''),
        features: [features[0], ...features.slice(2)],
      };
    }

    return {
      label: '',
      features,
    };
  };

  const getPlanCtaLabel = (planName: string) =>
    planName === 'Enlightened' ? tp('pricing.talk_to_our_team') :
    planName === 'Explorer' ? t('pricing.upgrade_to_explorer') :
    planName === 'Scholar' ? t('pricing.choose_scholar') :
    planName === 'Sage' ? t('pricing.become_sage') : t('pricing.get_started');

  // Handle currency change from dropdown
  const handleCurrencyChange = (newCurrency: string, _newMultiplier: number) => {
    setCurrency(newCurrency);
  };

  // Initialize currency from localStorage
  useEffect(() => {
    try {
      const savedCurrency = localStorage.getItem('currency');
      if (savedCurrency) {
        setCurrency(savedCurrency);
      }
    } catch (error) {
      console.error('Error reading currency from localStorage:', error);
      // Defaults are already set in useState
    }
  }, []);

  const plans = currency === 'INR' ? plansINR : plansUSD;
  const topUpPlan = currency === 'INR' ? topUpPlanINR : topUpPlanUSD;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoggedIn(!!user);
      if (user) {
        try {
          getBillingGateway().catch(() => {});
          // Ensure App Check token is available before Firestore operations
          if (appCheck) {
            try {
              await getToken(appCheck);
              console.log("✅ App Check token verified for HomePricing Firestore operations");
            } catch (appCheckError) {
              console.error("❌ App Check token verification failed in HomePricing:", appCheckError);
              // Continue anyway in development, but log the error
              if (!import.meta.env.DEV) {
                throw appCheckError;
              }
            }
          }
          
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          const userData = userDoc.exists() ? userDoc.data() : {};

          const subscriptionName = userData.subscription_name || '';
          const hits = userData.hits || '0';
          const subscriptionDate = userData.date ? new Date(userData.date) : new Date();
          const period = userData.period || 1;

          setCurrentPlan(subscriptionName);
          setRemainingHits(hits);
          setIsCurrentPlanMonthly(period === 1);

          const now = new Date();
          const daysDiff = Math.floor((now.getTime() - subscriptionDate.getTime()) / (1000 * 60 * 60 * 24));
          const isPaidPlan = subscriptionName && subscriptionName !== 'free_plan';
          const isPlanExpired = isPaidPlan && ((period === 1 && daysDiff > 30) || (period === 12 && daysDiff > 365));

          setIsExpired(isPlanExpired);

          if (isPlanExpired && subscriptionName !== 'free_plan') {
            const renewalStatus = await checkSubscriptionRenewal(user.uid);
            if (renewalStatus.status === 'updated') {
              const updatedDoc = await getDoc(userDocRef);
              const updatedData = updatedDoc.exists() ? updatedDoc.data() : {};
              setCurrentPlan(updatedData.subscription_name || 'Expired');
              setRemainingHits(updatedData.hits || '0');
              setIsExpired(false);
              setIsCurrentPlanMonthly(updatedData.period === 1);
            }
          }
        } catch (error) {
          console.error('Error fetching subscription data:', error);
          setCurrentPlan('');
          setRemainingHits('0');
          setIsExpired(false);
          setIsCurrentPlanMonthly(false);
        }
      } else {
        setCurrentPlan('');
        setRemainingHits('0');
        setIsExpired(false);
        setIsCurrentPlanMonthly(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handlePlanSelect = (plan: Plan) => {
    // Always route Enlightened to contact page, regardless of login state
    if (plan.name === 'Enlightened') {
      navigate('/contact-us');
      return;
    }
    if (!isLoggedIn) {
      if (plan.name === 'free_plan') {
        navigate('/sign-up');
      } else {
        navigate('/sign-up?pricing=true');
        return;
      }
    } else {
      localStorage.setItem('plan', JSON.stringify({ plan, isAnnual }));
      navigate('/plan-details', { state: { plan, isAnnual } });
    }
  };

  const handleSwitchToYearly = (plan: Plan) => {
    if (plan.name === 'Enlightened') {
      navigate('/contact-us');
      return;
    }
    if (!isLoggedIn) {
      navigate('/sign-up?pricing=true');
      return;
    }
    localStorage.setItem('plan', JSON.stringify({ plan, isAnnual: true }));
    navigate('/plan-details', { state: { plan, isAnnual: true, switchToYearly: true } });
  };

  const handleTopUpSelect = () => {
    if (!isLoggedIn) {
      navigate(`/sign-up?pricing=true`);
      return;
    }
    navigate('/plan-details', { state: { plan: topUpPlan, isAnnual: false } });
  };

  return (
    <div className="sl-pricing-shell bg-white py-10">
      <div style={{ position: 'relative', overflow: 'visible' }} className="container mx-auto px-6">
        {/* Image 1: homepage-2.png */}
        <img
          className="sl-pricing-ornament hidden xl:block"
          style={{
            position: 'absolute',
            transform: 'translate(-50%, -50%)',
            left: '15%',
            top: '5.5%',
            width: '200px',
          }}
          src="/homepage-2.png"
          alt="Homepage 2"
        />
        {/* Image 2: homepage-1.png */}
        <img
          className="sl-pricing-ornament hidden xl:block"
          style={{
            position: 'absolute',
            transform: 'translate(-50%, -50%)',
            right: '0%',
            top: '4.5%',
            width: '200px',
          }}
          src="/homepage-1.png"
          alt="Homepage 1"
        />
        {isLoggedIn && (
          <div className="text-center mb-8">
            <p className="text-lg text-gray-700">
              {t('pricing.current_subscription')} <span className="font-semibold">{currentPlan === 'free_plan' ? t('pricing.free') : currentPlan}</span> | 
              {t('pricing.remaining_tokens')}: <span className="font-semibold">{remainingHits}</span>
              {isExpired && (
                <span className="text-red-500 ml-2"> ({t('pricing.expired_renew')})</span>
              )}
            </p>
          </div>
        )}
        {/* Keep pricing intro below the fixed nav while scrolling. Previous zIndex 1001 sat above the header (999). */}
        <div className="text-center mb-16 sl-pricing-intro" style={{ position: 'relative', zIndex: 0 }}>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('pricing.pricing_title')}</h2>
          <p className="text-gray-600 text-lg">
            {t('pricing.pricing_description')}
          </p>
          <div className="flex justify-center items-center gap-4 mt-6">
            <div className="inline-flex border border-gray-300 rounded-full p-1 bg-gray-100">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  !isAnnual ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                {t('pricing.monthly')}
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`flex items-center gap-2 rounded-full py-2 pl-6 pr-2.5 text-sm font-medium transition-colors ${
                  isAnnual ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                <span>{t('pricing.annually')}</span>
                <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm ring-1 ring-white/25">
                  {t('pricing.annual_save_badge', { defaultValue: 'Save 17%' })}
                </span>
              </button>
            </div>
            <div className="hidden" aria-hidden="true">
              <CurrencyDropdown onCurrencyChange={handleCurrencyChange} />
            </div>
          </div>
        </div>
        
        {/* Pricing Bridge Text */}
        <div className="text-center mb-12">
          <p 
            className="text-center max-w-2xl mx-auto" 
            style={{
              fontFamily: "'Crimson Text', 'Georgia', serif",
              fontSize: "18px",
              fontWeight: 300,
              color: "#64748B",
              fontStyle: "italic",
              letterSpacing: "0.01em",
              lineHeight: 1.6,
              margin: "40px auto",
              maxWidth: "650px"
            }}
          >
            {t('pricing.pricing_bridge')}
          </p>
        </div>
        
        {/* Primary tiers: Free, Explorer, Scholar */}
        <div className="grid gap-5 md:grid-cols-3 mb-5">
          {plans.slice(0, 3).map((plan) => {
            const isCurrentUserPlan = isLoggedIn && currentPlan === plan.name;
            const featureGroup = getPresentedFeatures(plan.name);
            const showSwitchToYearly =
              isCurrentUserPlan && isCurrentPlanMonthly && plan.name !== 'free_plan' && plan.name !== 'Enlightened';
            const showPrimaryCta = showSwitchToYearly || (!isCurrentUserPlan && !(plan.name === 'free_plan' && isLoggedIn));
            const buttonLabel = showSwitchToYearly ? t('pricing.switch_to_yearly') : getPlanCtaLabel(plan.name);
            const buttonAction = showSwitchToYearly ? () => handleSwitchToYearly(plan) : () => handlePlanSelect(plan);

            return (
              <div
                key={plan.name}
                className={`sl-pricing-card group relative rounded-2xl border bg-white p-8 text-gray-900 transition duration-200 ease-out hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,11,0.10),0_2px_4px_rgba(10,10,11,0.04)] ${
                  plan.popular
                    ? 'sl-pricing-card-popular border-blue-200 bg-gradient-to-b from-[#fdfdff] to-white shadow-[0_14px_38px_-18px_rgba(37,99,235,0.35)] hover:-translate-y-1'
                    : 'sl-pricing-card-standard border-gray-200'
                } ${isCurrentUserPlan ? 'ring-4 ring-emerald-200' : ''}`}
              >
                {plan.popular && (
                  <span className="absolute -top-4 left-1/2 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-gray-950 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                    {tp('pricing.most_popular')}
                  </span>
                )}
                {isCurrentUserPlan && (
                  <span className="sl-pricing-current-plan absolute right-5 top-5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    {t('pricing.current_plan')}
                  </span>
                )}

                <h3 className="mb-2 text-2xl font-semibold tracking-normal">
                  {plan.name === 'free_plan' ? t('pricing.free') : t(`pricing.${plan.name.toLowerCase()}`)}
                </h3>
                {/* Secondary card copy: keep upright, but lighter/smaller than plan names and prices.
                   Revert to text-base text-gray-500 if this feels too quiet. */}
                <p className="sl-pricing-muted mb-7 text-sm font-normal text-[#8A8A90]">{getPlanSubtitle(plan.name)}</p>

                <div className="mb-7">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold leading-none tracking-normal">
                      {getPlanDisplayPrice(plan)}
                    </span>
                    {getPlanDisplayPeriod(plan.name) ? (
                      <span className="sl-pricing-muted ml-2 text-gray-500">{getPlanDisplayPeriod(plan.name)}</span>
                    ) : null}
                  </div>
                  {plan.name === 'free_plan' ? (
                    <p className="sl-pricing-muted mt-3 text-sm text-gray-500">{tp('pricing.no_credit_card_required')}</p>
                  ) : (
                    <p className="mt-3 flex items-center gap-2 text-sm font-normal text-orange-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-orange-600" />
                      {tp('pricing.launch_offer_limited_period')}
                    </p>
                  )}
                </div>

                {showPrimaryCta ? (
                  <button
                    onClick={buttonAction}
                    className={`mb-8 inline-flex w-full items-center justify-center gap-2 rounded-lg border px-6 py-3 font-medium tracking-[-0.01em] transition ${
                      plan.name === 'Explorer' || showSwitchToYearly
                        ? 'border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700'
                        : 'sl-pricing-button-secondary border-gray-200 bg-white text-gray-950 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {buttonLabel}
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </button>
                ) : null}

                <hr className="sl-pricing-divider mb-4 border-0 border-t-[0.5px] border-slate-200" />
                <p className="sl-pricing-section-label mb-4 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
                  {featureGroup.label}
                </p>
                <ul className="space-y-3">
                  {featureGroup.features.map((feature, index) => (
                    <li
                      key={index}
                      className="sl-pricing-soft flex items-start gap-3 text-[15px] leading-[1.55] text-slate-700"
                    >
                      <StrokeCheck />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Secondary tiers: Sage and Enterprise */}
        <div className="grid gap-5 lg:grid-cols-2">
          {plans.slice(3).map((plan) => {
            const isCurrentUserPlan = isLoggedIn && currentPlan === plan.name;
            const isEnterprise = plan.name === 'Enlightened';
            const featureGroup = getPresentedFeatures(plan.name);
            const showSwitchToYearly =
              isCurrentUserPlan && isCurrentPlanMonthly && plan.name !== 'free_plan' && plan.name !== 'Enlightened';
            const buttonLabel = showSwitchToYearly ? t('pricing.switch_to_yearly') : getPlanCtaLabel(plan.name);
            const buttonAction = showSwitchToYearly ? () => handleSwitchToYearly(plan) : () => handlePlanSelect(plan);

            return (
              <div
                key={plan.name}
                className={`sl-pricing-card group relative overflow-hidden rounded-2xl border p-8 transition duration-200 ease-out hover:-translate-y-0.5 md:grid md:grid-cols-2 md:gap-8 ${
                  isEnterprise
                    ? 'sl-pricing-card-enterprise border-gray-950 bg-gray-950 text-white shadow-[0_20px_40px_-18px_rgba(10,10,11,0.45)] hover:border-gray-800'
                    : 'sl-pricing-card-standard border-gray-200 bg-gradient-to-br from-white to-gray-50 text-gray-900 hover:border-gray-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,11,0.10)]'
                } ${isCurrentUserPlan ? 'ring-4 ring-emerald-200' : ''}`}
              >
                {isEnterprise ? (
                  <div className="pointer-events-none absolute right-0 top-0 h-60 w-60 bg-[radial-gradient(circle_at_top_right,rgba(234,88,12,0.18),transparent_60%)]" />
                ) : null}
                {isCurrentUserPlan && (
                  <span className="sl-pricing-current-plan absolute right-5 top-5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    {t('pricing.current_plan')}
                  </span>
                )}

                <div className="relative mb-8 md:mb-0">
                  {isEnterprise ? (
                    <span className="mb-5 inline-flex rounded-full bg-orange-950/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-400">
                      {tp('pricing.enterprise_label')}
                    </span>
                  ) : null}
                  <h3 className="mb-2 text-2xl font-semibold tracking-normal">
                    {t(`pricing.${plan.name.toLowerCase()}`)}
                  </h3>
                  <p className={`sl-pricing-muted mb-7 text-sm font-normal ${isEnterprise ? 'text-white/65' : 'text-[#8A8A90]'}`}>
                    {isEnterprise ? getPlanDescription(plan.name) : getPlanSubtitle(plan.name)}
                  </p>

                  <div className="mb-7">
                    <div className="flex items-baseline">
                      <span className={`${isEnterprise ? 'text-3xl' : 'text-4xl'} font-bold leading-none tracking-normal`}>
                        {getPlanDisplayPrice(plan)}
                      </span>
                      {getPlanDisplayPeriod(plan.name) ? (
                        <span className={`sl-pricing-muted ml-2 ${isEnterprise ? 'text-white/60' : 'text-gray-500'}`}>
                          {getPlanDisplayPeriod(plan.name)}
                        </span>
                      ) : null}
                    </div>
                    {!isEnterprise ? (
                      <p className="mt-3 flex items-center gap-2 text-sm font-normal text-orange-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-orange-600" />
                        {tp('pricing.launch_offer_limited_period')}
                      </p>
                    ) : null}
                  </div>

                  {/* Pricing CTA tuning: use font-medium + slight negative tracking for a lighter premium feel.
                     Revert to font-semibold and remove tracking-[-0.01em] if this reads too light. */}
                  <button
                    onClick={buttonAction}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-medium tracking-[-0.01em] transition md:max-w-60 focus:outline-none ${
                      isEnterprise
                        ? 'border-0 bg-orange-600 text-white shadow-none hover:bg-orange-700 focus-visible:ring-2 focus-visible:ring-orange-300/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950'
                        : 'sl-pricing-button-secondary border border-gray-200 bg-white text-gray-950 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {buttonLabel}
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </button>
                </div>

                <div className="relative">
                  <hr className={`sl-pricing-divider mb-4 border-0 border-t-[0.5px] ${isEnterprise ? 'border-white/15' : 'border-slate-200'}`} />
                  <p
                    className={`sl-pricing-section-label mb-4 text-[11px] font-medium uppercase tracking-[0.14em] ${
                      isEnterprise ? 'text-white/60' : 'text-slate-400'
                    }`}
                  >
                    {featureGroup.label}
                  </p>
                  <ul className="space-y-3">
                    {featureGroup.features.map((feature, index) => (
                      <li
                        key={index}
                        className={`sl-pricing-soft flex items-start gap-3 text-[15px] leading-[1.55] ${
                          isEnterprise ? 'text-white/90' : 'text-slate-700'
                        }`}
                      >
                        <StrokeCheck />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {isLoggedIn && (currentPlan === 'Explorer' || currentPlan === 'Scholar' || currentPlan === 'Sage') ? (
          <div className="sl-pricing-topup mt-5 rounded-2xl border border-gray-200 bg-white p-8 text-gray-900 transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,11,0.10)]">
            <div className="grid gap-6 md:grid-cols-[1fr_1.2fr_auto] md:items-center">
              <div>
                <h3 className="mb-2 text-2xl font-semibold">{t('pricing.top_up')}</h3>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold leading-none">{topUpPlan.monthlyPrice}</span>
                  <span className="sl-pricing-muted ml-2 text-gray-500">{getPlanDisplayPeriod(topUpPlan.name)}</span>
                </div>
              </div>
              <ul className="space-y-3">
                {getTranslatedFeatures('Top-Up').map((feature, index) => (
                  <li key={index} className="sl-pricing-soft flex items-start gap-3 text-[15px] leading-[1.55] text-slate-700">
                    <StrokeCheck />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleTopUpSelect}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-6 py-3 font-medium tracking-[-0.01em] text-white transition hover:border-blue-700 hover:bg-blue-700 md:w-auto"
              >
                {t('pricing.top_up_now')}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}

        {/* Serious Seeker Section */}
        <div className="sl-serious-seeker-bleed">
          <SeriousSeekerSection />
        </div>

        <HomeTestimonials />

      </div>
    </div>
  );
}
